import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { EFeatureToggles, EGlueResource, EUserRoles } from '../../configurations/common';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { I18nService } from '../../services';
import { AnalyticsService } from '../../analytics/analytics.service';
import { CheckoutFacade } from '../../facades/checkout.facade';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { ICartAttributes, ICartItemWithDetail, ICartRule } from '../../models/cart.models';
import {
  IAddress,
  IMaterialMasterNumbersPerItem,
  IPriceDisputingPerItem,
  ISystemDetails,
} from '../../models/common.models';
import { ICheckoutUpdate } from '../../models/checkout.models';
import { CartUtils } from '../../utils/cart.utils';
import { ObjectUtils } from '../../utils/object.utils';
import { CustomerFacade } from '../../facades/customer.facade';

@Component({
  selector: 'app-spare-part-delivery-details',
  templateUrl: './spare-part-delivery-details.component.html',
  styleUrls: ['./spare-part-delivery-details.component.scss'],
  standalone: false,
})
export class SparePartDeliveryDetailsComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  userData: any;
  isUserDataLoading: boolean = true;

  loadingCartDataInProgress: boolean = true;
  isCartEmpty: boolean = true;
  cartDataLoaded: boolean = false;
  cartItemsDataLoaded: boolean = false;
  creatingOrderInProgress: boolean = false;
  cartId!: string;
  cartAttributes!: ICartAttributes;
  cartItemsWithDetails!: Array<ICartItemWithDetail>;
  cartRules!: Array<ICartRule>;
  systemDetails!: ISystemDetails;
  priceDisputingPerItem!: IPriceDisputingPerItem[];
  materialMasterNumbersPerItem: IMaterialMasterNumbersPerItem[] = [];

  isOnContactDetailsSection: boolean = false;
  orderDetailsData!: {shipToAddress: IAddress | undefined, billToAddress: IAddress | undefined};
  isSaturdayShipment: boolean = false;
  addressChanged: boolean = false;
  isCustomerBusinessPartner: boolean = false;
  isPriceDisputingActive: boolean = false;
  private isSAPDiscountFeatureEnabled: boolean = false;

  private unsubscribe$: Subject<void> = new Subject<void>();
  private cartIncludedResources!: any[];

  ngOnInit(): void {
    this.selectSAPDiscountFeatureEnabled();
    this.selectCustomerData();
    this.selectCustomerDataLoading();
    this.selectLoadingCartDataInProgress();
    this.selectIsCartEmpty();
    this.loadCartData();
    this.selectCartItemsWithDetails();
    this.selectIsCustomerBusinessPartner();
    this.selectPriceDisputingPerItem();
    this.selectIsPriceDisputingActive();
    this.selectMaterialMasterNumbersPerItem();
    this.selectIsSaturdayShipmentSelected();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.customerFacade.clearAttentionTo();
  }

  backToHomepage(): void {
    this.router.navigate([this.i18nService.getCurrentLocale()]).then();
  }

  proceedToOrderDetails(): void {
    this.isOnContactDetailsSection = false;
  }

  proceedToContactDetails(orderDetailsData: {shipToAddress: IAddress | undefined, billToAddress: IAddress | undefined}): void {
    this.orderDetailsData = orderDetailsData;
    this.isOnContactDetailsSection = true;
  }

  createOrder(contactDetailsData: any): void {
    this.creatingOrderInProgress = true;

    let checkoutUpdate: ICheckoutUpdate = {
      data: {
        type: EGlueResource.CHECKOUT_UPDATE,
        attributes: {
          idCart: this.cartId,
          isAddressSavingSkipped: true,
          pointOfContact: {...contactDetailsData.pointOfContact},
          priceDisputingPerItem: this.priceDisputingPerItem,
          materialMasterNumbers: this.materialMasterNumbersPerItem,
        },
      },
    };

    if (this.orderDetailsData?.billToAddress) {
      checkoutUpdate = this._extendCheckoutUpdateWithBillTo(checkoutUpdate);
    }

    if ((this.cartAttributes?.shippingAddress && this.orderDetailsData?.shipToAddress &&
      !ObjectUtils.isEqual(
        this.cartAttributes.shippingAddress,
        this.orderDetailsData.shipToAddress,
      )) || this.isCustomerBusinessPartner) {
      // set new shipTo address if it is different from quote shipTo address
      checkoutUpdate = this._extendCheckoutUpdateWithShipTo(checkoutUpdate);
    }

    this.checkoutFacade.postCheckoutUpdate(checkoutUpdate).pipe(
      take(1),
    ).subscribe({
      next: _ => {
        this.creatingOrderInProgress = false;
        this.checkoutFacade.actionPutCartIdOrderApprove(this.cartId);
        this.router.navigate(['/order-review/', this.cartId]).then();
      },
      error: _ => {
        this.creatingOrderInProgress = false;
      },
    });
  }

  /**
   * Extends the checkout update object with the billing address.
   *
   * @private
   *
   * @param {ICheckoutUpdate} checkoutUpdate
   * @returns {ICheckoutUpdate}
   */
  private _extendCheckoutUpdateWithBillTo(checkoutUpdate: ICheckoutUpdate): ICheckoutUpdate {
    return {
      ...checkoutUpdate,
      data: {
        ...checkoutUpdate.data,
        attributes: {
          ...checkoutUpdate.data.attributes,
          billingAddress: this.updateAddress(this.orderDetailsData?.billToAddress),
        },
      },
    };
  }

  /**
   * Extends the checkout update object with the shipping address.
   *
   * @private
   *
   * @param {ICheckoutUpdate} checkoutUpdate
   * @returns {ICheckoutUpdate}
   */
  private _extendCheckoutUpdateWithShipTo(checkoutUpdate: ICheckoutUpdate): ICheckoutUpdate {
    return {
      ...checkoutUpdate,
      data: {
        ...checkoutUpdate.data,
        attributes: {
          ...checkoutUpdate.data.attributes,
          shippingAddress: this.updateAddress(this.orderDetailsData?.shipToAddress),
        },
      },
    };
  }

  updateAddress(address: IAddress | undefined): IAddress {
    return {
      salutation: address?.salutation || 'Ms',
      firstName: address?.firstName || 'empty',
      lastName: address?.lastName || 'empty',
      address1: address?.address1 || 'empty',
      address2: address?.address2 || 'empty',
      address3: '',
      zipCode: address?.zipCode || 'empty',
      city: address?.city || 'empty',
      country: address?.country || 'United States',
      iso2Code: address?.iso2Code || 'US',
      phone: address?.phone || 'empty',
      company: address?.company || 'empty',
      state: address?.state || 'empty',
      sapId: address?.sapId,
      idCustomerAddress: address?.idCustomerAddress,
      idCompanyUnitAddress: address?.idCompanyUnitAddress,
      isDefaultBilling: false,
      isDefaultShipping: false,
      isCustom: address?.isCustom ?? false,
    };
  }

  updateIsSaturdayShipment(isSaturdayShipment: boolean): void {
    this.isSaturdayShipment = isSaturdayShipment;
  }

  private selectIsCustomerBusinessPartner(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isCustomerBusinessPartner => {
      this.isCustomerBusinessPartner = isCustomerBusinessPartner;
    });
  }

  private selectCustomerData(): void {
    this.customerFacade.selectCustomerData().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(data => {
      this.userData = data;
      if (data.companyRoles.includes(EUserRoles.Viewer)) {
        this.backToHomepage();
      }
    });
  }

  private selectCustomerDataLoading(): void {
    this.customerFacade.selectCustomerDataLoading().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isDataLoading => {
      this.isUserDataLoading = isDataLoading;
    });
  }

  private selectIsCartEmpty(): void {
    this.marketingFacade.selectIsCartEmpty().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isCartEmpty = state;
    });
  }

  private loadCartData(): void {
    this.marketingFacade.getCurrentCartItems().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartData => {
      if (!cartData) {
        return;
      }

      this.cartId = cartData.data.id;
      this.cartAttributes = cartData.data.attributes;
      this.cartIncludedResources = cartData.included;
      this.cartRules = this.cartIncludedResources.filter(include => include.type === 'cart-rules');
      this.systemDetails = cartData.data.attributes.systemDetails;
      this.cartDataLoaded = true;

      this.analyticsService.setCartId(this.cartId);
      this.analyticsService.setProducts(cartData);
    });

    this.analyticsService.trackPageReady('spare part - delivery details', PageTypes.DELIVERY_DETAILS_PAGE, 'concrete-products');
  }

  private selectCartItemsWithDetails(): void {
    this.marketingFacade.selectCartItemsWithDetails().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartItemsWithDetails => {
      if (cartItemsWithDetails?.length > 0) {
        this.cartItemsWithDetails = cartItemsWithDetails;
        this.cartItemsDataLoaded = true;
        this.cartItemsWithDetails = CartUtils.mapAvailabilitiesToCartItems(
          this.cartItemsWithDetails, this.cartIncludedResources,
        );
      }
    });
  }

  private selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => {
      this.loadingCartDataInProgress = loadingCartDataInProgress;
    });
  }

  /**
   * Select price disputing as array of all items
   * which had price disputing set (from ShopCart state).
   *
   * @private
   *
   */
  private selectPriceDisputingPerItem(): void {
    this.marketingFacade.selectPriceDisputing()
      .pipe(takeUntil(this.unsubscribe$)).subscribe(priceDisputingPerItem => {
      this.priceDisputingPerItem = priceDisputingPerItem;
    });
  }

  /**
   * Select material master numbers as array of all items
   * which had material master number set (from ShopCart state).
   *
   * @private
   *
   */
  private selectMaterialMasterNumbersPerItem(): void {
    this.marketingFacade.selectAllMaterialMasterNumbersInCart(this.cartId)
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe(materialMasterNumbers => {
        this.materialMasterNumbersPerItem = materialMasterNumbers ?? [];
      });
  }

  /**
   * Select if price disputing functionality is active (based on the company roles).
   *
   * @private
   *
   */
  private selectIsPriceDisputingActive(): void {
    this.configurationFacade.isPriceDisputingActiveForCurrentRole().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isPriceDisputingActive => {
      this.isPriceDisputingActive = isPriceDisputingActive;
    });
  }

  /**
   * Select if SAP discount feature is enabled.
   *
   * @private
   *
   */
  private selectSAPDiscountFeatureEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.DISCOUNT_CODE_ENABLED).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isSAPDiscountEnabled => {
      this.isSAPDiscountFeatureEnabled = isSAPDiscountEnabled;
    });
  }

  /**
   * Select if saturday shipping method is selected.
   *
   * @private
   *
   */
  private selectIsSaturdayShipmentSelected(): void {
    this.customerFacade.isSaturdayShipmentSelected().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((isSaturdayShipment) => {
      this.isSaturdayShipment = isSaturdayShipment;
    });
  }

  /**
   * Check if discount is applied on cart and SAP discount feature is enabled
   *
   * @returns {boolean}
   */
  hasSapDiscounts(): boolean {
    return this.isSAPDiscountFeatureEnabled && (this.cartAttributes?.discounts?.length > 0);
  }
}
